<template>
  <div>
    <div class="filtering">
      <span
        class="block w-3/4 mx-auto text-xl font-bold uppercase text-center sm:text-left"
        >{{ $t('Campaigns') }}</span
      >
      <div
        class="flex w-3/4 mx-auto my-4 gap-x-4 justify-center sm:justify-start"
      >
        <button
          id="filter-raffle"
          class="filter-button px-2 py-4"
          :class="{ active: currentFilterType === 'raffle' }"
          @click="filterCampaigns('raffle')"
        >
          {{ $t('Raffle') }}
        </button>
        <button
          id="filter-cashback"
          class="filter-button px-2 py-4"
          :class="{ active: currentFilterType === 'cashback' }"
          @click="filterCampaigns('cashback')"
        >
          {{ $t('Cashback') }}
        </button>
      </div>
    </div>
    <div v-if="campaignsLoaded" class="campaigns">
      <div v-show="activeCampaigns.length > 0">
        <span
          class="block w-3/4 mx-auto mt-8 pt-4 text-xl font-bold uppercase text-center sm:text-left border-t border-black"
          >{{ $t('Active Campaigns') }}</span
        >
        <div
          class="grid grid-cols-1 sm:grid-cols-2 gap-6 content-start justify-center w-3/4 mx-auto my-4 bg-white"
        >
          <div
            v-for="campaign in activeCampaigns"
            :key="campaign.id"
            class="active-campaign flex items-center justify-between w-full gap-5 flex-col shadow-xl shadow-indigo-100/50 rounded-xl"
          >
            <img
              :src="campaign.bannerDesktop"
              :alt="`${campaign.name} Banner`"
              class="hidden sm:block rounded-t-xl"
            />
            <img
              :src="campaign.bannerMobile"
              :alt="`${campaign.name} Banner`"
              class="block sm:hidden rounded-t-xl"
            />
            <div class="flex flex-col items-center gap-5 pt-4 text-center">
              <span class="text-xl">{{ campaignRange(campaign) }}</span>
              <span class="text-xl">{{ campaign.name }}</span>
            </div>
            <img v-if="campaign.retailerLogo" :src="campaign.retailerLogo" alt="Retailer Logo" class="retailer-logo">
            <button-component
              :id="`participate-button-${campaign.id}`"
              class="campaignBtn w-fit mb-4"
              text="Participate"
              @click.stop="participate(campaign)"
            />
          </div>
        </div>
      </div>
      <div v-show="endedCampaigns.length > 0">
        <span
          class="block w-3/4 mx-auto mt-8 pt-4 text-xl font-bold uppercase text-center sm:text-left border-t border-black"
          >{{ $t('Past Campaigns') }}</span
        >
        <div
          class="grid grid-cols-1 sm:grid-cols-2 gap-6 content-start justify-center w-3/4 mx-auto my-4 bg-white"
        >
          <div
            v-for="campaign in endedCampaigns"
            :key="campaign.id"
            class="ended-campaign flex items-center justify-between w-full gap-5 flex-col shadow-xl shadow-indigo-100/50 rounded-xl"
          >
            <img
              :src="campaign.bannerDesktop"
              :alt="`${campaign.name} Banner`"
              class="hidden sm:block rounded-t-xl opacity-50"
            />
            <img
              :src="campaign.bannerMobile"
              :alt="`${campaign.name} Banner`"
              class="block sm:hidden rounded-t-xl opacity-50"
            />
            <span class="text-xl">{{ campaignRange(campaign) }}</span>
            <button-component
              :id="`terms-button-${campaign.id}`"
              class="campaignBtn w-fit mb-4 opacity-50 hover:opacity-100"
              text="Terms"
              @click.stop="terms(campaign)"
            />
          </div>
        </div>
      </div>
      <div v-if="readyForVerificationCampaigns.length > 0">
        <span
          class="block w-3/4 mx-auto mt-8 pt-4 text-xl font-bold uppercase text-center sm:text-left border-t border-black"
          >{{ $t('Ready for Verification Campaigns') }}</span
        >
        <div
          class="grid grid-cols-1 sm:grid-cols-2 gap-6 content-start justify-center w-3/4 mx-auto my-4 bg-white"
        >
          <div
            v-for="campaign in readyForVerificationCampaigns"
            :key="campaign.id"
            class="qa-campaign flex items-center justify-between w-full gap-5 flex-col shadow-xl shadow-indigo-100/50 rounded-xl"
          >
            <img
              :src="campaign.bannerDesktop"
              :alt="`${campaign.name} Banner`"
              class="hidden sm:block rounded-t-xl"
            />
            <img
              :src="campaign.bannerMobile"
              :alt="`${campaign.name} Banner`"
              class="block sm:hidden rounded-t-xl"
            />
            <div class="flex flex-col items-center gap-5 p-4 text-center">
              <span class="text-xl">{{ campaignRange(campaign) }}</span>
              <span class="text-xl">{{ campaign.name }}</span>
            </div>
            <button-component
              :id="`participate-button-${campaign.id}`"
              class="campaignBtn w-fit mb-4"
              text="Participate"
              @click.stop="participate(campaign)"
            />
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <div class="flex items-center justify-center gap-5 h-screen text-xl text-gray-800">
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-500"></div>
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-500"></div>
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-500"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { useCampaignStore } from '@/stores'
import { format } from 'date-fns'
import { useRouter } from 'vue-router'

const campaignsLoaded = ref(false)
const filteredCampaigns = ref([])
const activeCampaigns = ref([])
const endedCampaigns = ref([])
const readyForVerificationCampaigns = ref([])
const currentFilterType = ref('All')
const router = useRouter()

const campaignStore = useCampaignStore()

onMounted(async () => {
  try {
    const res = await campaignStore.fetchCampaigns()
    campaignsLoaded.value = res
    filterCampaigns('All')
  } catch (error) {
    console.error('Error fetching campaigns:', error)
  }
})

const filterCampaigns = type => {
  activeCampaigns.value = []
  endedCampaigns.value = []

  if (type === currentFilterType.value) {
    currentFilterType.value = 'All'
    filteredCampaigns.value = campaignStore.campaigns
  } else {
    currentFilterType.value = type
    filteredCampaigns.value = campaignStore.campaigns.filter(c => {
      return c.type === type
    })
  }
  const tz = 'Europe/Bucharest';

  // Get current date/time in Romania
  const currentDate = new Date(new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date()));

  filteredCampaigns.value.forEach(campaign => {
    // Get end of campaign day in Romania
    const endDate = new Date(new Date(campaign.uploadEndDate).toLocaleString('en-US', { timeZone: tz }));
    endDate.setHours(23, 59, 59, 999);

    if (campaign.campaignStatus == 'ready-for-verification') {
      readyForVerificationCampaigns.value.push(campaign)
    } else if (endDate < currentDate) {
      endedCampaigns.value.push(campaign)
    } else {
      activeCampaigns.value.push(campaign)
    }
  })
}

const campaignRange = campaign => {
  const start = formatDate(campaign.startDate)
  const end = formatDate(campaign.endDate)
  return start + ' - ' + end
}

const participate = campaign => {
  campaignStore.setActiveCampaign(campaign)
  router.push({
    name: 'CampaignPage',
    params: { campaign: campaign.domain }
  })
}

const terms = campaign => {
  // Create link and add random query parameter to prevent caching
  const link = `${campaign.tnc}?_=${new Date().getTime()}`
  window.open(link, '_blank')
}

// TODO: Uncomment when retailers page is ready
// const retailers = campaign => {
//   campaignStore.setActiveCampaign(campaign)
//   router.push({ name: 'RetailersPage', params: { campaign: campaign.domain } })
// }

const formatDate = function (date, formatString = 'dd.MM.yyyy') {
  return format(new Date(date), formatString)
}
</script>

<style scoped lang="scss">
.filter-button {
  min-width: 120px;
  cursor: pointer;
  user-select: none;
  text-align: center;
  border: 1px solid #4f46e5;
  color: #4f46e5;
  border-radius: 0.75rem;
  transition: background-color 0.3s, color 0.3s;
  &:hover {
    background-color: #4f46e5;
    color: white;
  }
  &.active {
    background-color: #4f46e5;
    color: white;
  }
}

img {
  width: 100%;
}

.retailer-logo {
  width: 55px;
  height: auto;
}
</style>
